import { useMemo, useState } from 'react'
import {
  Button,
  DataTable,
  FilterBar,
  Icon,
  Money,
  SelectChip,
  StatusChip,
  SummaryBanner,
  SyncStatus,
  type ColumnDef,
} from '../components'
import { useMe } from '../lib/use-me'
import { canDelete, canEditOperational } from '../lib/permissions'
import { useCounterparties } from '../lib/api/counterparties'
import { useLicenseTariffs } from '../lib/api/license-tariffs'
import {
  flattenLicenses,
  useBootstrapLicenses,
  useLicenseDebtSummary,
  useLicenses,
} from '../lib/api/licenses'
import type { Currency, License, LicenseStatusFilter, LicenseDebtSummary } from '../lib/api/types'
import { fmtMoney } from '../lib/money'
import { classifyDuePeriod, type DuePeriod } from '../lib/due-period'
import { LicenseDetailDrawer } from './license-detail-drawer'

const STATUS_ITEMS: { id: LicenseStatusFilter; name: string }[] = [
  { id: 'debt', name: 'Борг' },
  { id: 'purchased', name: 'Куплено' },
  { id: 'expired', name: 'Прострочено' },
]

// "Closes within …" dropdown — same horizons as the debt-summary buckets.
const EXPIRING_ITEMS: { id: string; name: string }[] = [
  { id: '7', name: '≤ 7 днів' },
  { id: '14', name: '≤ 14 днів' },
  { id: '30', name: '≤ 30 днів' },
]

export function LicensesPage() {
  const me = useMe()
  const canEdit = canEditOperational(me.data?.role)
  const allowDelete = canDelete(me.data?.role)
  const [status, setStatus] = useState<LicenseStatusFilter | null>('debt')
  const [expiringWithin, setExpiringWithin] = useState<number | null>(null)
  // Due-date filter driven by clicking a chip in the debt banner. Mutually
  // exclusive with the "Закінчується" dropdown (both narrow by due date).
  const [duePeriod, setDuePeriod] = useState<DuePeriod | null>(null)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const counterparties = useCounterparties()
  const tariffs = useLicenseTariffs()
  const counterpartyMap = useMemo(
    () => new Map((counterparties.data ?? []).map((c) => [c.id, c.name])),
    [counterparties.data],
  )
  const tariffMap = useMemo(
    () => new Map((tariffs.data ?? []).map((t) => [t.id, t.name])),
    [tariffs.data],
  )
  const tariffCostCurrencyMap = useMemo(
    () => new Map((tariffs.data ?? []).map((t) => [t.id, t.costCurrency])),
    [tariffs.data],
  )
  const today = new Date().toISOString().slice(0, 10)
  const validToBefore =
    expiringWithin != null
      ? new Date(Date.now() + expiringWithin * 24 * 3600 * 1000).toISOString().slice(0, 10)
      : undefined

  const list = useLicenses({
    commitmentStatus: status ?? undefined,
    validToBefore,
  })

  // "Our debt to Bitrix24" total across all licenses (they aren't bound to one
  // of our organisations, so there's no org scope to apply).
  const debtSummary = useLicenseDebtSummary()

  const licenses = useMemo(() => {
    let filtered = flattenLicenses(list.data)
    // Expiry only matters while we still owe the vendor — a purchased license is
    // closed out, so the "closes within N days" filter excludes purchased ones.
    if (expiringWithin != null) filtered = filtered.filter((l) => l.commitmentStatus === 'debt')
    // Banner period chip: keep only the outstanding-debt rows that fall in the
    // clicked due-date window, matching exactly the figure behind the chip.
    if (duePeriod != null) {
      filtered = filtered.filter((l) => licenseDuePeriod(l, today) === duePeriod)
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      filtered = filtered.filter(
        (l) =>
          l.title.toLowerCase().includes(q) ||
          l.bitrixLicenseId.toLowerCase().includes(q),
      )
    }
    return filtered
  }, [list.data, expiringWithin, duePeriod, today, search])

  // Toggle a due-date window from the banner. Selecting one clears the dropdown
  // (they'd otherwise fight over the same axis) and forces the debt scope, since
  // the banner figures only count outstanding debt.
  const handlePeriodToggle = (period: DuePeriod) => {
    if (duePeriod === period) {
      setDuePeriod(null)
      return
    }
    setDuePeriod(period)
    setExpiringWithin(null)
    if (status !== 'debt') setStatus('debt')
  }

  const bootstrap = useBootstrapLicenses()
  const hasFilters =
    status != null || expiringWithin != null || duePeriod != null || !!search.trim()

  const columns: ColumnDef<License>[] = [
    {
      id: 'id',
      header: 'ID',
      width: 90,
      sortValue: (l) => l.bitrixLicenseId,
      cell: (l) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-3)' }}>
          {l.bitrixLicenseId}
        </span>
      ),
    },
    {
      id: 'title',
      header: 'Назва',
      sortValue: (l) => l.title,
      cell: (l) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="key-round" size={12} color="var(--accent)" />
          {l.title}
        </span>
      ),
    },
    {
      id: 'client',
      header: 'Клієнт',
      width: 160,
      cellClassName: 'fg2',
      sortValue: (l) => counterpartyMap.get(l.counterpartyId) ?? null,
      cell: (l) => counterpartyMap.get(l.counterpartyId) ?? <span className="fg3">—</span>,
    },
    {
      id: 'tariff',
      header: 'Тариф',
      width: 140,
      cellClassName: 'fg3',
      sortValue: (l) => (l.tariffId ? (tariffMap.get(l.tariffId) ?? null) : null),
      cell: (l) => (l.tariffId ? (tariffMap.get(l.tariffId) ?? '—') : '—'),
    },
    {
      id: 'salePrice',
      header: 'Сума',
      width: 110,
      thClassName: 'num',
      cellClassName: 'num',
      sortValue: (l) => l.salePrice,
      cell: (l) => <Money amount={l.salePrice} currency={l.saleCurrency} />,
    },
    {
      id: 'vendor',
      header: 'Вендор',
      width: 200,
      cellClassName: 'fg2',
      sortValue: (l) => l.vendorName ?? '',
      cell: (l) => {
        if (!l.vendorName) return <span className="fg3">—</span>
        const ccy = l.tariffId ? tariffCostCurrencyMap.get(l.tariffId) : undefined
        return (
          <div>
            <div>{l.vendorName}</div>
            {l.amountPaidToVendor > 0 && ccy && (
              <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                сплачено {fmtMoney(l.amountPaidToVendor, ccy)}
              </div>
            )}
          </div>
        )
      },
    },
    {
      id: 'debt',
      header: 'Наш борг',
      width: 150,
      thClassName: 'num',
      cellClassName: 'num',
      sortValue: (l) => l.costUah ?? l.costPrice ?? null,
      cell: (l) => {
        if (!l.debtActive || l.costPrice == null || l.costCurrency == null) {
          return <span className="fg3">—</span>
        }
        return (
          <div>
            <div style={{ color: 'var(--accent)' }}>
              <Money amount={l.costPrice} currency={l.costCurrency} />
            </div>
            {/* A vendor promo was recorded by hand — show what it was cut from. */}
            {l.discountPercent > 0 && l.costListPrice != null && (
              <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>
                <s>{fmtMoney(l.costListPrice, l.costCurrency)}</s> −{l.discountPercent}%
              </div>
            )}
            {l.costUah != null && (
              <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>≈ {fmtMoney(l.costUah, 'UAH')}</div>
            )}
          </div>
        )
      },
    },
    {
      id: 'validTo',
      header: 'Дійсна до',
      width: 130,
      sortValue: (l) => l.validTo,
      cell: (l) => {
        if (!l.validTo) return <span className="fg3">—</span>
        const daysDiff = Math.round(
          (new Date(l.validTo).getTime() - new Date(today).getTime()) / 86400000,
        )
        const overdue = daysDiff < 0
        // Validity urgency only matters while we still owe the vendor. A purchased
        // license is closed out, so the lapsed/expiring note is just noise — show
        // the date alone.
        const note =
          l.commitmentStatus !== 'debt'
            ? ''
            : overdue
              ? `прострочено ${Math.abs(daysDiff)}д`
              : daysDiff <= 30
                ? `за ${daysDiff}д`
                : ''
        return (
          <div>
            <div className="num">{l.validTo}</div>
            {note && (
              <div style={{ fontSize: 11, color: overdue ? 'var(--danger-fg)' : 'var(--fg-3)' }}>
                {note}
              </div>
            )}
          </div>
        )
      },
    },
    {
      id: 'status',
      header: 'Статус',
      width: 150,
      sortValue: (l) => l.commitmentStatus,
      cell: (l) => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <StatusChip status={l.commitmentStatus} />
          {l.expired && <StatusChip status="expired" />}
        </div>
      ),
    },
  ]

  return (
    <>
      <FilterBar
        right={
          canEdit ? (
            <Button
              variant="secondary"
              size="sm"
              icon="refresh-cw"
              onClick={() => bootstrap.mutate()}
              disabled={bootstrap.isPending}
            >
              {bootstrap.isPending ? 'Синхронізація…' : 'Синхронізувати з B24'}
            </Button>
          ) : undefined
        }
      >
        <SelectChip
          label="Статус"
          items={STATUS_ITEMS}
          selectedId={status}
          onChange={(id) => setStatus(id as LicenseStatusFilter | null)}
        />
        <SelectChip
          label="Закінчується"
          items={EXPIRING_ITEMS}
          selectedId={expiringWithin != null ? String(expiringWithin) : null}
          onChange={(id) => {
            setExpiringWithin(id != null ? Number(id) : null)
            setDuePeriod(null)
          }}
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Пошук назви або B24 ID…"
          className="input"
          style={{ maxWidth: 220 }}
        />
      </FilterBar>

      <div className="content">
        <SyncStatus
          isPending={bootstrap.isPending}
          isError={bootstrap.isError}
          error={bootstrap.error}
          data={bootstrap.data}
        />
        {debtSummary.data && debtSummary.data.count > 0 && (
          <DebtBanner
            summary={debtSummary.data}
            activePeriod={duePeriod}
            onPeriodToggle={handlePeriodToggle}
          />
        )}
        {list.isLoading && <SkeletonRows />}
        {!list.isLoading && licenses.length === 0 && (
          <EmptyStateLocal
            canEdit={canEdit}
            isFiltered={hasFilters}
            onSync={() => bootstrap.mutate()}
            onReset={() => {
              setStatus(null)
              setExpiringWithin(null)
              setDuePeriod(null)
              setSearch('')
            }}
            bootstrapPending={bootstrap.isPending}
          />
        )}
        {!list.isLoading && licenses.length > 0 && (
          <div className="card">
            <DataTable
              tableKey="licenses"
              columns={columns}
              rows={licenses}
              rowKey={(l) => l.id}
              onRowClick={(l) => setSelectedId(l.id)}
              rowClassName={(l) => (l.id === selectedId ? 'selected' : undefined)}
            />
            {list.hasNextPage && (
              <div style={{ padding: 12, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? 'Завантаження…' : 'Завантажити ще'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedId && (
        <LicenseDetailDrawer
          licenseId={selectedId}
          canEdit={canEdit}
          canDelete={allowDelete}
          onClose={() => setSelectedId(null)}
        />
      )}
    </>
  )
}

// The banner figures only count outstanding debt (debt-active, still owed),
// bucketed by the license's expiry date. Mirror that here so a period chip
// filters to exactly its rows; anything else returns null and is filtered out.
function licenseDuePeriod(l: License, today: string): DuePeriod | null {
  if (!l.debtActive || l.costPrice == null) return null
  if (l.costPrice - l.amountPaidToVendor <= 0) return null
  return classifyDuePeriod(l.validTo, today)
}

const BUCKET_LABELS: Record<number, string> = {
  7: '≤ 7 дн',
  14: '≤ 14 дн',
  30: '≤ 30 дн',
}

function DebtBanner({
  summary,
  activePeriod,
  onPeriodToggle,
}: {
  summary: LicenseDebtSummary
  activePeriod: DuePeriod | null
  onPeriodToggle: (period: DuePeriod) => void
}) {
  // Total owed in the tariff's native currency (essentially EUR), shown first,
  // with its UAH equivalent. No vendor breakdown.
  const nativeTotal = (Object.entries(summary.byCurrency) as [Currency, number][])
    .filter(([, amount]) => amount > 0)
    .map(([currency, amount]) => fmtMoney(amount, currency))
    .join(' · ')
  return (
    <SummaryBanner
      icon="wallet"
      title="Наш борг перед Bitrix24"
      totals={[
        { label: 'Загалом', value: nativeTotal || fmtMoney(0, 'EUR') },
        { label: '≈ у гривні', value: fmtMoney(summary.debtUah, 'UAH'), tone: 'var(--accent)' },
      ]}
      countText={`${summary.count} ${plural(summary.count, 'ліцензія', 'ліцензії', 'ліцензій')} до сплати`}
      fxIncomplete={summary.fxIncomplete}
      periodsLabel="Терміни оплати"
      overdueUah={summary.overdueUah}
      overdueCount={summary.overdueCount}
      buckets={summary.buckets}
      bucketLabels={BUCKET_LABELS}
      laterLabel="пізніше"
      laterUah={summary.laterUah}
      activePeriod={activePeriod}
      onPeriodToggle={onPeriodToggle}
    />
  )
}

// Ukrainian plural: 1 / 2-4 / 5+ (ignoring 11-14 teens edge — close enough for a counter).
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return many
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}

function SkeletonRows() {
  return (
    <div className="card" style={{ padding: 20 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 36,
            background: 'var(--bg-3)',
            borderRadius: 4,
            marginBottom: 6,
            opacity: 0.6,
          }}
        />
      ))}
    </div>
  )
}

function EmptyStateLocal({
  canEdit,
  isFiltered,
  onSync,
  onReset,
  bootstrapPending,
}: {
  canEdit: boolean
  isFiltered: boolean
  onSync: () => void
  onReset: () => void
  bootstrapPending: boolean
}) {
  if (isFiltered) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ color: 'var(--fg-2)', marginBottom: 12 }}>
          Ліцензій з такими фільтрами не знайдено.
        </div>
        <Button variant="secondary" size="sm" onClick={onReset}>
          Скинути фільтри
        </Button>
      </div>
    )
  }
  return (
    <div className="card" style={{ padding: 32, textAlign: 'center' }}>
      <div style={{ color: 'var(--fg-2)', marginBottom: 12 }}>
        Ліцензії з&apos;являться після першої синхронізації з Bitrix24.
      </div>
      {canEdit && (
        <Button
          variant="primary"
          size="sm"
          icon="refresh-cw"
          onClick={onSync}
          disabled={bootstrapPending}
        >
          {bootstrapPending ? 'Синхронізація…' : 'Синхронізувати'}
        </Button>
      )}
    </div>
  )
}
